import React, {useState, useEffect} from 'react';

import { AppBar, Toolbar, IconButton, Hidden, Typography, Grid, CircularProgress, Paper, Avatar, Table, TableCell, TableBody, TableRow, TableHead, makeStyles } from '@material-ui/core';

import ArrowBack from '@material-ui/icons/ArrowBackIos';

import BillingStatement from '../../entities/BillingStatement';
import PaymentMethod from '../../entities/PaymentMethod';

import { AccountService } from '../../services/AccountService';
import { ServerResponse } from '../../services/server/OldWebClient';

import { UserAvatar } from '../../components/UserAvatar';
import { ChangeCardDialog } from './changeCardDialog';
import { AccountInfo } from './components/accountInfo';
import { SubscriptionInfo } from './components/subscriptionInfo';
import { BillingStatementsTable } from './components/billingStatementsTable';
import { CancelSubscriptionDialog } from './components/cancelSubscriptionDialog';
import { RefundSubscriptionDialog } from './components/refundSubscriptionDialog';
import { UpdateCardDialog } from './components/updateCardDialog';
import { SpecialOfferDialog } from './components/specialOfferDialog';
import { ConfirmationDialog } from './components/confirmationDialog';
import { AccountSnackBar } from './components/accountSnackBar';
import { useUser } from '../../utilities/useUser';
import { useIsMobile } from '../../components/Mui3WidthProvider';
import { useHistory } from 'react-router-dom';
import { useAuthenticatedUser } from '../../utilities/useAuthenticatedUser';

const useStyles = makeStyles((theme) => ({
    container: {
        overflow: "hidden"
    },
    appBar: {
        color: '#6b6b6b',
        background: '#FFFFFF',
        boxShadow: "unset",
        paddingTop: '4px'
    },
    title: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        width: '100%',
        textOverflow: 'ellipsis'
    },
    grow: {
        flex: 1
    },
    avatar: {
        margin: '0 auto',
        backgroundColor: theme.palette.primary.light,
        width: 100,
        height: 100,
        fontSize: '40px',
        marginBottom: theme.spacing(1),
    },
    contentSection: {
        position: 'absolute',
        left: '0px',
        right: '0px',
        bottom: '0px',
        top: `calc(${theme.mixins.toolbar.minHeight}px + 15px)`,
        overflowY: "auto",
        background: '#efefef',
    },
    section: {
        margin: '0 auto',
        marginTop: theme.spacing(4),
        [theme.breakpoints.up("sm")]: {
            width: "60%",
            marginBottom: theme.spacing(6),
            padding: theme.spacing(4),
        },
        [theme.breakpoints.down("xs")]: {
            width: "100%",
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            marginBottom: 30,
            marginTop: 10,
        },
    },
}));


export function Settings() {
    const history = useHistory();
    const userContext = useUser();
    const user = useAuthenticatedUser();
    const makerSubscription = user.makerSubscription;
    const classes = useStyles();
    const isMobile = useIsMobile(); 

    const [loading, setLoading] = useState<boolean>(false);
    const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
    const [openRefundDialog, setOpenRefundDialog] = useState<boolean>(false);
    const [openUpdateCardDialog, setOpenUpdateCardDialog] = useState<boolean>(false);
    const [openChangeCardDialog, setOpenChangeCardDialog] = useState<boolean>(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
    const [openSpecialOfferDialog, setOpenSpecialOfferDialog] = useState<boolean>(false);
    const [billingStatements, setBillingStatements] = useState<BillingStatement[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

    useEffect(() => {
        document.title = "Sermon Maker - Settings";
        setLoading(true);
        getBillingStatements();
        getPaymentMethod();
        setLoading(false);
    }, [])

    const getBillingStatements = async() => {
        let billingStatementResponse = await AccountService.getBillingStatements();
        if (ServerResponse.isSuccess(billingStatementResponse)) {
            setBillingStatements(billingStatementResponse.data);
        }
    }

    const getPaymentMethod = async() => {
        let result = await AccountService.getPaymentMethod();
        if (result.success && result.data) {
            setPaymentMethod(result.data)
        }
    }

    const onUpdatePayment = async (updatedPaymentMethod:PaymentMethod) => {
        setPaymentMethod(updatedPaymentMethod);
    }

    const onChangeCard = (updatedPaymentMethod:PaymentMethod) => {
        setPaymentMethod(updatedPaymentMethod);
        setOpenChangeCardDialog(false);
    }

    const toggleUpdateCardDialog = () => setOpenUpdateCardDialog(!openUpdateCardDialog);
    const toggleCancelDialog = () => setOpenCancelDialog(!openCancelDialog)
    const toggleRefundDialog = () => setOpenRefundDialog(!openRefundDialog)
    const toggleChangeCardDialog = () => setOpenChangeCardDialog(!openChangeCardDialog);
    const toggleSpecialOfferDialog = () => setOpenSpecialOfferDialog(!openSpecialOfferDialog);
    const toggleConfirmationDialog = () => setOpenConfirmationDialog(!openConfirmationDialog);
    const toggleShowErrorMessage = () => setShowErrorMessage(!showErrorMessage);

    return (
        <div className={classes.container}>
            <AppBar position="static" className={classes.appBar}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="Open drawer"
                        onClick={() => history.goBack()}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Hidden mdUp>
                        <div style={{ flexGrow: 1, width: '50%' }}>
                            <Typography variant='h6' className={classes.title} color="inherit">Settings</Typography>
                        </div>
                    </Hidden>
                    <Hidden mdDown>
                        <div style={{ flexGrow: 1 }}>
                            <Typography variant='h6' className={classes.title} color="inherit">Settings</Typography>
                        </div>
                    </Hidden>
                    <div className={classes.grow} />
                    <Grid container justifyContent="flex-end" style={{ paddingRight: 15, height: '40px' }}>
                        <Grid item>
                            <UserAvatar />
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            {
                loading
                &&
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress style={{ marginTop: "50px" }} />
                    <br />
                    <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                        Loading...
                    </Typography>
                </div>
            }
            {
                !loading
                &&
                <div className={classes.contentSection}>
                    <Grid container direction="row" justifyContent="center" alignItems="flex-start">
                        <Grid item>
                            <div style={{ marginTop: 20 }}>
                                {
                                    user.profileImageUrl
                                        ?
                                        <Avatar className={classes.avatar} src={user.profileImageUrl} />
                                        :
                                        <Avatar className={classes.avatar}>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</Avatar>
                                }
                                <Typography variant="h5" align="center">Sermon Maker Settings</Typography>
                                <Typography variant="subtitle1" align="center">Manage your subscription and view billing statements</Typography>
                            </div>
                        </Grid>
                    </Grid>
                    <AccountInfo />
                    <SubscriptionInfo 
                        paymentMethod={paymentMethod} 
                        openUpdateCardDialog={toggleUpdateCardDialog}
                        openCancelAndRefundDialog={toggleRefundDialog}
                        openCancelDialog={toggleCancelDialog}
                        openChangeCardDialog={toggleChangeCardDialog}
                    />
                    {
                        billingStatements.length > 0
                        &&
                        <>
                            {!isMobile &&
                                <Paper className={classes.section} elevation={2}>
                                    <Typography variant="h5">Billing Statements</Typography>

                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Amount</TableCell>
                                                <TableCell align="right">Invoice Number</TableCell>
                                                <TableCell align="right">Date Created</TableCell>
                                                <TableCell align="right" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {billingStatements.map((row, index) => {
                                                return <BillingStatementsTable key={index} row={row} />
                                            })}
                                        </TableBody>
                                    </Table>

                                </Paper>
                            }
                            {isMobile &&
                                <>
                                    <Typography style={{ marginTop: 12 }} variant="h5">Billing Statements</Typography>
                                    {billingStatements.map((row, index) => {
                                        return <BillingStatementsTable key={index} row={row} />
                                    })}
                                </>
                            }
                        </>
                    }
                </div>
            }
            {   user.inAdminImpersonationMode
                    &&
                    <RefundSubscriptionDialog 
                    toggleRefundDialog={toggleRefundDialog} 
                    openRefundDialog={openRefundDialog} 
                    onSubscriptionChanged={userContext.updateUser}
                    setErrorMessage={(message:string) => setErrorMessage(message)}
                    toggleShowErrorMessage={toggleShowErrorMessage}/>
                }
            {
                paymentMethod
                &&
                <UpdateCardDialog 
                    toggleUpdateCardDialog={toggleUpdateCardDialog}
                    paymentMethod={paymentMethod}
                    openUpdateCardDialog={openUpdateCardDialog}
                    updateCardInfo={onUpdatePayment}
                    setErrorMessage={(message:string) => setErrorMessage(message)}
                    toggleShowErrorMessage={toggleShowErrorMessage}
                />
            }
            {
                makerSubscription
                &&
                !makerSubscription.cancellationDate
                &&
                <ChangeCardDialog
                    isOpen={openChangeCardDialog}
                    onClose={toggleChangeCardDialog}
                    onSuccess={newPaymentMethod => onChangeCard(newPaymentMethod)}
                />
            }
            <SpecialOfferDialog
                openSpecialOfferDialog={openSpecialOfferDialog}
                toggleSpecialOfferDialog={toggleSpecialOfferDialog}
                onSubscriptionChanged={userContext.updateUser}
                toggleConfirmationDialog={toggleConfirmationDialog}
                setErrorMessage={(message:string) => setErrorMessage(message)}
                toggleShowErrorMessage={toggleShowErrorMessage}/>
            { makerSubscription
                &&
                <>
                    <CancelSubscriptionDialog 
                        toggleCancelDialog={toggleCancelDialog} 
                        toggleSpecialOfferDialog={toggleSpecialOfferDialog}
                        onSubscriptionChanged={userContext.updateUser}
                        openCancelDialog={openCancelDialog} 
                        setErrorMessage={(message:string) => setErrorMessage(message)}
                        toggleShowErrorMessage={toggleShowErrorMessage}
                    />
                    <ConfirmationDialog 
                        openConfirmationDialog={openConfirmationDialog}
                        toggleConfirmationDialog={toggleConfirmationDialog}
                    />
                </>
            }
            {  showErrorMessage
                &&
                <AccountSnackBar
                    snackbarOpen={showErrorMessage}
                    onClose={toggleShowErrorMessage}
                    snackbarMessage={errorMessage}
                />
            }
        </div>
    );
}